import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Table } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import PageHeader from 'components/PageHeader';
import { getIssuanceBatchApi, uniformErrorMessage } from 'api/services/uniformManagement';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge, getIssuanceStatusLabel } from '../../constants';
import './issuanceBatchPrint.css';
import useStickyIdentity from '../../components/useStickyIdentity';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

const ISSUANCE_LIST_PATH = '/uniform-management/issuance';

/** Scopes issuanceBatchPrint.css to this page — see the comment at the top of
 *  that file for why a globally-injected stylesheet needs a body-level hook. */
const PRINT_SCOPE_CLASS = 'uniform-issue-card-page';

/** The lines table is titled by a real heading rather than a styled <div>, so
 *  the grid has an accessible name; only one instance of this page is ever
 *  mounted (it is a route), so a constant id is safe. */
const ITEMS_HEADING_ID = 'issuance-batch-items-heading';

// Matches UniformIssuanceBatch.KIND_CHOICES on the backend — the API only
// ever sends the raw code ("SINGLE"/"BULK"), not a display label.
const kindLabels = { SINGLE: 'Single', BULK: 'Bulk' };
const kindLabel = (kind) => kindLabels[kind] || kind;

// Module scope rather than inline: these are constant, and the meta grid
// rebuilt them on every render of every field.
const backLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  fontWeight: 600,
  color: '#6B7280',
  textDecoration: 'none',
};

const shellStyle = {
  border: '1px solid #F1F2F4',
  borderRadius: 12,
  overflow: 'hidden',
  background: '#FFFFFF',
};

const summaryStyle = {
  padding: '16px 16px 4px',
  background: '#FAFAFA',
  borderBottom: '1px solid #F1F2F4',
};

const linesHeadStyle = {
  padding: '12px 16px 0',
};

// A heading element carrying the label type spec, with the browser's default
// h2 margins and size reset off it.
const sectionHeadingStyle = { margin: '0 0 4px' };

const footStyle = {
  padding: '10px 16px',
  borderTop: '1px solid #F1F2F4',
  fontSize: 12,
  color: '#6B7280',
};

// Same type spec as the table headers and the transfers expanded panel
// (11 / 600 / 0.04em / uppercase), so the page has one label style, not two.
const metaLabelStyle = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#6B7280',
  marginBottom: 4,
};

const metaValueStyle = { fontSize: 13, color: '#1F1F1F', lineHeight: 1.5 };

const tabularNums = { fontVariantNumeric: 'tabular-nums' };

const empNoStyle = { fontSize: 12, color: '#6B7280' };

const Meta = ({ label, children }) => (
  <div className="mb-3">
    <div style={metaLabelStyle}>{label}</div>
    <div style={metaValueStyle}>{children || <span style={{ color: '#6B7280' }}>&mdash;</span>}</div>
  </div>
);

/** The status helper returns a style object, not an element — this is the one
 *  place that turns it into the tag, so the colours stay owned by the module's
 *  `statusBadgeMap` and the wording by `issuanceStatusOptions`. */
const StatusTag = ({ status, count }) => (
  <span style={{ ...getStatusBadge(status), display: 'inline-block', whiteSpace: 'nowrap' }}>
    {getIssuanceStatusLabel(status)}
    {count === undefined ? null : <span style={tabularNums}> &middot; {count}</span>}
  </span>
);

/** Spec §29 "Uniform Issue Card": one batch — who issued what, to whom, from
 *  where and when. Printable via a scoped print block that drops the app
 *  chrome and the on-screen-only controls.
 *
 *  Laid out as ONE detail surface (summary strip, then one lines table)
 *  rather than the header-card-plus-a-card-per-employee stack it used to be:
 *  at 40 employees that was 41 separate bordered blocks and 40 separate antd
 *  tables, each with its own header row, which is both a wall of chrome and a
 *  real mount cost.
 *
 *  Grouping is by `rowSpan` on the Employee column rather than antd's
 *  `expandable`. An expandable table would show 40 employee rows and zero
 *  items until each is opened — but this page IS the issue card: its whole
 *  job is to show every line at once, and it has to print. rowSpan keeps one
 *  flat, fully-visible, printable table while still naming each employee
 *  exactly once, and costs the reader no interaction at all. */
const IssuanceBatchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);

  // Guarded with `ignore` so a slow response for a stale batch id (navigating
  // away before it resolves) can't clobber a newer fetch, including the
  // loading-state reset.
  useEffect(() => {
    let ignore = false;
    setLoading(true);
    getIssuanceBatchApi(id).then((response) => {
      if (ignore) return;
      setBatch(response.data?.data || null);
    }).catch((e) => {
      if (ignore) return;
      toast.error(uniformErrorMessage(e, 'Failed to load issuance batch'), toastOpts);
      navigate(ISSUANCE_LIST_PATH);
    }).finally(() => {
      if (!ignore) setLoading(false);
    });
    return () => { ignore = true; };
  }, [id, navigate]);

  // Scopes the imported print stylesheet to this page. Added to <body> because
  // the chrome it hides (.sidebar, .header) sits outside .page-wrapper, so no
  // class within the page could reach it.
  useEffect(() => {
    document.body.classList.add(PRINT_SCOPE_CLASS);
    return () => document.body.classList.remove(PRINT_SCOPE_CLASS);
  }, []);

  /**
   * Flatten the batch's lines into one grouped, rowSpan-annotated list.
   *
   * Lines keep their own snapshot fields (name, emp_no, CASA, position) rather
   * than reading the employee's current profile, so the card still shows what
   * was true at issuance time after a transfer or a promotion.
   *
   * `_rowSpan` is the group's length on each employee's first line and 0 on
   * the rest, which is exactly what antd's `onCell` wants; the counts roll the
   * per-line statuses up for the summary strip, since a BATCH has no status of
   * its own on the backend — only its lines do.
   */
  const { rows, employeeCount, statusEntries } = useMemo(() => {
    const lines = batch?.lines || [];
    const order = [];
    const byUser = {};
    lines.forEach((line) => {
      const key = line.company_user;
      if (!byUser[key]) { byUser[key] = []; order.push(key); }
      byUser[key].push(line);
    });

    const flat = [];
    order.forEach((key) => {
      byUser[key].forEach((line, index) => {
        flat.push({ ...line, _rowSpan: index === 0 ? byUser[key].length : 0 });
      });
    });

    const counts = new Map();
    lines.forEach((line) => {
      if (!line.status) return;
      counts.set(line.status, (counts.get(line.status) || 0) + 1);
    });

    return { rows: flat, employeeCount: order.length, statusEntries: Array.from(counts.entries()) };
  }, [batch]);

  const totalQuantity = useMemo(
    () => rows.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0),
    [rows],
  );

  // Above the loading guard below: a hook cannot sit after an early return.
  const pinLeft = useStickyIdentity();

  if (loading || !batch) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="text-center py-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
        </div>
      </div>
    );
  }

  const lineColumns = [
    {
      // Pinned. Verified in a browser that the `rowSpan` below survives it:
      // rc-table gives the fixed column the SAME `<td>`, so one sticky cell
      // spans the employee's whole block and keeps their name beside every
      // line of it while Size, Qty and Status scroll past.
      title: 'Employee',
      dataIndex: 'company_user_name',
      ...pinLeft(280),
      // The spanned cell swallows its own clicks: `onRow` below opens the
      // issuance line, and this cell covers a whole employee's block, so a
      // click low down in a tall cell would otherwise open the FIRST line of
      // the group — a row the user was not pointing at.
      onCell: (record) => ({
        rowSpan: record._rowSpan,
        // `cursor` overrides `.clickable-row`'s pointer for this one cell,
        // since it deliberately does nothing on click — only the employee
        // link inside it does.
        style: { verticalAlign: 'top', cursor: 'default' },
        onClick: (e) => e.stopPropagation(),
      }),
      render: (_, record) => (
        <div>
          {record.company_user ? (
            <Link to={`/employees/${record.company_user}`} className="table-link">
              {record.company_user_name || '—'}
            </Link>
          ) : (record.company_user_name || '—')}
          {/* CASA belongs here, not only in the summary strip. The strip shows
              the BATCH's `cost_center`, a nullable FK that a BULK run spanning
              several CASAs leaves empty — and the per-employee CASA snapshot
              was information the old per-employee section header carried.
              Dropping it would have lost it from the page entirely. */}
          <div style={empNoStyle}>
            {record.company_user_emp_no || '—'}
            {record.position_name ? ` · ${record.position_name}` : ''}
            {record.casa_name ? ` · ${record.casa_name}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Uniform',
      dataIndex: 'uniform_item_name',
      width: 260,
      render: (value) => value || <span className="text-muted">&mdash;</span>,
    },
    {
      title: 'Size',
      dataIndex: 'size_code',
      width: 110,
      // Code, not name: the code is what the warehouse picks by, and it is the
      // value the batch actually recorded.
      render: (value) => (value ? <span style={{ fontWeight: 600 }}>{value}</span> : <span className="text-muted">&mdash;</span>),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      width: 90,
      align: 'right',
      render: (value) => <span style={tabularNums}>{value ?? 0}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 140,
      render: (status) => (status ? <StatusTag status={status} /> : <span className="text-muted">&mdash;</span>),
    },
  ];

  return (
    <div className="page-wrapper">
      {/* Print rules live in issuanceBatchPrint.css, imported above, rather
          than in an injected <style> block — they now include an overflow
          reset that the inline version was missing entirely. */}
      <div className="content container-fluid">
        <div className="no-print">
          <Link to={ISSUANCE_LIST_PATH} style={backLinkStyle}>
            <i className="fa fa-arrow-left" aria-hidden="true" />
            Issuances
          </Link>
        </div>

        <PageHeader
          className="mt-2"
          title={batch.reference || `Batch #${batch.id}`}
          subtitle="Uniform issue card"
          actions={(
            <span className="no-print">
              <Button label="Print" icon="fa fa-print" className="btn btn-outline-primary btn-sm"
                onClick={() => window.print()} />
            </span>
          )}
        />

        <div className="print-card" style={shellStyle}>
          <div style={summaryStyle}>
            <div className="row">
              <div className="col-md-3 col-sm-6"><Meta label="Issue Date">{formatDateHandler(batch.issuance_date)}</Meta></div>
              <div className="col-md-3 col-sm-6"><Meta label="Cost Center">{batch.cost_center_name}</Meta></div>
              <div className="col-md-3 col-sm-6"><Meta label="Warehouse">{batch.warehouse_name}</Meta></div>
              <div className="col-md-3 col-sm-6"><Meta label="Issued By">{batch.issued_by_name}</Meta></div>
              <div className="col-md-3 col-sm-6"><Meta label="Kind">{kindLabel(batch.kind)}</Meta></div>
              <div className="col-md-3 col-sm-6">
                <Meta label="Lines">
                  <span style={tabularNums}>{rows.length}</span>
                  {' across '}
                  <span style={tabularNums}>{employeeCount}</span>
                  {employeeCount === 1 ? ' employee' : ' employees'}
                </Meta>
              </div>
              <div className="col-md-6">
                {/* Labelled "Line Status", not "Status": UniformIssuanceBatch
                    has no status column — only UniformIssuance does — so this
                    is a roll-up of the lines, and a mixed batch says so
                    instead of picking one code to stand for the whole run. */}
                <Meta label="Line Status">
                  {statusEntries.length === 0 ? null : (
                    <span className="d-inline-flex flex-wrap gap-2">
                      {statusEntries.map(([status, count]) => (
                        <StatusTag key={status} status={status} count={count} />
                      ))}
                    </span>
                  )}
                </Meta>
              </div>
              {batch.notes ? (
                <div className="col-12">
                  {/* pre-wrap, because notes are typed in a textarea and their
                      line breaks are usually the only structure they have. */}
                  <Meta label="Notes"><span style={{ whiteSpace: 'pre-wrap' }}>{batch.notes}</span></Meta>
                </div>
              ) : null}
            </div>
          </div>

          <section style={linesHeadStyle} aria-labelledby={ITEMS_HEADING_ID}>
            <h2 id={ITEMS_HEADING_ID} style={{ ...metaLabelStyle, ...sectionHeadingStyle }}>
              Items issued
            </h2>
            <Table
              columns={lineColumns}
              dataSource={rows}
              rowKey={(record) => record.id}
              pagination={false}
              size="small"
              tableLayout="fixed"
              scroll={{ x: 880 }}
              rowClassName="clickable-row"
              locale={{ emptyText: 'This batch has no lines.' }}
              onRow={(record) => ({
                onClick: () => navigate(`/uniform-management/issuance/${record.id}`),
              })}
            />
          </section>

          {rows.length > 0 ? (
            <div style={footStyle} className="d-flex justify-content-between align-items-center">
              <span>{rows.length} {rows.length === 1 ? 'line' : 'lines'}</span>
              <span>
                Total quantity{' '}
                <strong style={{ ...tabularNums, color: '#1F1F1F' }}>{totalQuantity}</strong>
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default IssuanceBatchDetail;
